<template>
  <aside class="detail-recommend">
    <h2 class="detail-recommend__title">{{ sidebarTitle }}</h2>

    <div v-if="loading" class="detail-recommend__state">
      <div class="detail-recommend__spinner" />
      <p>加载推荐中...</p>
    </div>

    <p v-else-if="!list.length" class="detail-recommend__empty">
      暂无同类推荐
    </p>

    <ul v-else class="detail-recommend__list">
      <li
        v-for="item in list"
        :key="item.id"
        class="detail-recommend__item"
      >
        <a
          :href="detailHref(item.id)"
          class="detail-recommend__card"
        >
          <div
            class="detail-recommend__image"
            :style="getCardStyle(item)"
          >
            <img
              v-if="item.cover_img"
              :src="item.cover_img"
              :alt="item.name"
              class="detail-recommend__img"
            />
            <span v-else class="detail-recommend__name">{{ item.name }}</span>
          </div>
          <p class="detail-recommend__category">
            第{{ formatCategory(item.category_id) }}类-{{ item.category_name }}
          </p>
          <p v-if="item.fit_projects" class="detail-recommend__desc">
            {{ truncateText(item.fit_projects) }}
          </p>
          <p class="detail-recommend__price">
            <template v-if="item.price > 0">¥{{ formatPrice(item.price) }}</template>
            <template v-else>面议</template>
          </p>
        </a>
      </li>
    </ul>
  </aside>
</template>

<script>
import { fetchTrademarkList } from '@/api/trademark'
import { DETAIL_RECOMMEND_LIMIT } from '@/constants/detailPage'
import { detailHref as buildDetailHref } from '@/utils/pageNavigation'

const DESC_MAX_LENGTH = 36

export default {
  name: 'DetailRecommendSidebar',
  props: {
    detail: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      list: [],
      loading: false
    }
  },
  computed: {
    sidebarTitle() {
      if (!this.detail) return '人气商标'
      const cat = this.formatCategory(this.detail.category_id)
      return `${cat}类-${this.detail.category_name}`
    }
  },
  watch: {
    'detail.id': {
      immediate: true,
      handler() {
        this.loadRecommendations()
      }
    }
  },
  methods: {
    loadRecommendations() {
      if (!this.detail || !this.detail.category_id) {
        this.list = []
        return
      }

      this.loading = true

      fetchTrademarkList({
        category: String(this.detail.category_id),
        page: 1,
        pageSize: DETAIL_RECOMMEND_LIMIT + 1,
        sort: 'hot'
      })
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.list = []
            return
          }

          this.list = (result.data.list || [])
            .filter(item => item.id !== this.detail.id)
            .slice(0, DETAIL_RECOMMEND_LIMIT)
        })
        .catch(() => {
          this.list = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    getCardStyle(item) {
      if (item.cover_img) return {}
      return { background: item.color || 'var(--color-primary-light)' }
    },
    formatCategory(id) {
      return String(id).padStart(2, '0')
    },
    formatPrice(price) {
      if (!price || price <= 0) return '面议'
      if (price >= 10000) {
        return (price / 10000).toFixed(2) + '万'
      }
      return Math.round(price).toLocaleString('zh-CN')
    },
    truncateText(text) {
      if (!text) return ''
      const normalized = String(text).replace(/\s+/g, ' ').trim()
      if (normalized.length <= DESC_MAX_LENGTH) return normalized
      return `${normalized.slice(0, DESC_MAX_LENGTH)}...`
    },
    detailHref(id) {
      return buildDetailHref(id)
    }
  }
}
</script>

<style scoped>
.detail-recommend {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--space-md);
}

.detail-recommend__title {
  font-size: var(--font-size-md);
  font-weight: 700;
  color: var(--color-text-primary);
  padding-bottom: var(--space-sm);
  border-bottom: 1px solid var(--color-border-light);
  margin-bottom: var(--space-md);
}

.detail-recommend__state,
.detail-recommend__empty {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.detail-recommend__spinner {
  width: 28px;
  height: 28px;
  border: 2px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: detail-recommend-spin 0.8s linear infinite;
  margin-bottom: var(--space-sm);
}

@keyframes detail-recommend-spin {
  to { transform: rotate(360deg); }
}

.detail-recommend__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.detail-recommend__item + .detail-recommend__item {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border-light);
}

.detail-recommend__card {
  display: block;
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s;
}

.detail-recommend__card:hover {
  opacity: 0.88;
}

.detail-recommend__image {
  width: 100%;
  height: 120px;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: var(--space-sm);
}

.detail-recommend__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.detail-recommend__name {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-inverse);
  padding: var(--space-sm);
  text-align: center;
  word-break: break-all;
}

.detail-recommend__category {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--space-xs);
}

.detail-recommend__desc {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  line-height: 1.6;
  margin-bottom: var(--space-xs);
}

.detail-recommend__price {
  font-size: var(--font-size-md);
  font-weight: 700;
  color: var(--color-price);
}
</style>
